import React, { useEffect, useState, useRef } from "react";

interface FlashingValueProps {
  /** The value (text or number) to display */
  value: React.ReactNode;

  /** Determines if the text is green (true) or red (false) and which color it flashes. If null/undefined, stays neutral (white). */
  isPositive: boolean | null | undefined;

  /**
   * Optional: A specific value to watch for changes to trigger the flash.
   * If omitted, the component automatically flashes anytime `value` changes.
   */
  flashTrigger?: unknown;

  /** Additional Tailwind classes */
  className?: string;
}

export const FlashingValue: React.FC<FlashingValueProps> = ({
  value,
  isPositive,
  flashTrigger,
  className = "",
}) => {
  const [flashClass, setFlashClass] = useState("");

  // Use flashTrigger if provided, otherwise fallback to value
  const trigger = flashTrigger !== undefined ? flashTrigger : value;
  const prevTriggerRef = useRef(trigger);

  useEffect(() => {
    if (prevTriggerRef.current !== trigger) {
      // Trigger changed, initiate the flash animation
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFlashClass(
        isPositive === true
          ? "animate-flash-green"
          : isPositive === false
            ? "animate-flash-red"
            : "animate-flash-white",
      );

      // Remove the animation class after it completes (matches 0.5s CSS animation)
      const timer = setTimeout(() => {
        setFlashClass("");
      }, 500);

      prevTriggerRef.current = trigger;

      return () => clearTimeout(timer);
    }
  }, [trigger, isPositive]);

  // Determine base text color based on market sentiment
  const baseColorClass =
    isPositive === true
      ? "text-[#00a85f] dark:text-[#00a85f]"
      : isPositive === false
        ? "text-red-500 dark:text-red-400"
        : "text-gray-900 dark:text-white";

  return (
    <span
      className={`transition-colors rounded px-1 ${baseColorClass} ${flashClass} ${className}`}
    >
      {value}
    </span>
  );
};
